import {
  CustomReportVariantType,
  CustomReportVariants,
  getConfigFromSavedFilter,
  useSavedCustomReport,
} from "#batteries-included-components/Layouts/CustomReport/CustomReportDetailLayout.helpers";
import { useCalculatorFilters } from "#hooks/filters/useCalculatorFilters";
import {
  EQUIPMENT_FILTERS,
  useEquipmentCalculationFilters,
} from "#hooks/filters/useEquipmentFilters";
import {
  FLOW_FILTERS,
  useFlowCalculationFilters,
} from "#hooks/filters/useFlowFilters";
import { useParams } from "#routers/hooks";
import { type CustomReportsDetailRoutePath } from "#routes/reports/custom-reports/[reportVariant]/[reportId]/detail";
import { parseCustomReportConfiguration } from "#src/batteries-included-components/Layouts/CustomReport/CustomReportConfigurationPanel/CustomReportConfigurationPanel.helpers";
import { useFacilityCalculationFilters } from "#src/resources/filters";
import {
  FilterPanel,
  StorageKeys,
  useFilters,
} from "@validereinc/common-components";
import {
  AssetType,
  EquipmentStatus,
  FacilitySchema,
  FacilityStatus,
  FlowStatus,
} from "@validereinc/domain";
import React from "react";

const FacilityKeys = FacilitySchema.keyof().Enum;

export const CustomReportDetailFilterPanel = ({
  filterConfigStorageKey,
  configStorageKey,
  variant,
}: Pick<StorageKeys, "filterConfigStorageKey"> & {
  configStorageKey: string;
  variant: CustomReportVariantType;
}) => {
  const { reportId } =
    useParams<
      Exclude<typeof CustomReportsDetailRoutePath.pathParams, undefined>
    >();
  const [config] = useFilters(configStorageKey);
  const { assetType } = parseCustomReportConfiguration(config);
  const {
    data: savedFilter,
    query: { isLoading: isLoadingCustomReport },
  } = useSavedCustomReport(variant, reportId);

  const calculatorFilters = useCalculatorFilters();
  const equipmentFilters = useEquipmentCalculationFilters();
  const facilityFilters = useFacilityCalculationFilters();
  const flowFilters = useFlowCalculationFilters();

  let filterConfiguration;
  let defaultActiveKeys;
  let defaultFilterValues;

  switch (variant) {
    case CustomReportVariants.EMISSIONS:
      filterConfiguration = [
        ...equipmentFilters.filters,
        ...calculatorFilters.filters,
      ];
      defaultActiveKeys = [
        ...equipmentFilters.filterKeys,
        ...calculatorFilters.filterKeys,
      ];
      defaultFilterValues = {
        [EQUIPMENT_FILTERS.equipmentStatus.name]: [EquipmentStatus.ACTIVE],
      };
      break;
    case CustomReportVariants.RECORDS:
      filterConfiguration =
        assetType === AssetType.FLOW
          ? flowFilters.filters
          : assetType === AssetType.FACILITY
            ? facilityFilters.filters
            : assetType === AssetType.EQUIPMENT
              ? equipmentFilters.filters
              : [];
      defaultActiveKeys =
        assetType === AssetType.FLOW
          ? flowFilters.filterKeys
          : assetType === AssetType.FACILITY
            ? facilityFilters.filterKeys
            : assetType === AssetType.EQUIPMENT
              ? equipmentFilters.filterKeys
              : [];
      defaultFilterValues =
        assetType === AssetType.FLOW
          ? { [FLOW_FILTERS.flowStatus.name]: [FlowStatus.ACTIVE] }
          : assetType === AssetType.FACILITY
            ? {
                [`facility.${FacilityKeys.status}`]: [FacilityStatus.ACTIVE],
              }
            : assetType === AssetType.EQUIPMENT
              ? {
                  [EQUIPMENT_FILTERS.equipmentStatus.name]: [
                    EquipmentStatus.ACTIVE,
                  ],
                }
              : {};
      break;
  }

  const defaultValue = savedFilter
    ? getConfigFromSavedFilter(variant, savedFilter).filter
    : defaultFilterValues;

  if (isLoadingCustomReport) return null;

  return (
    <FilterPanel
      defaultActiveKeys={defaultActiveKeys}
      defaultValue={defaultValue}
      storageKey={filterConfigStorageKey}
      filters={filterConfiguration ?? []}
    />
  );
};
